import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { options, result, routes } from '../api'
import { useTranslation } from './i18n'
import Layout from './components/Layout'
import fetch from 'fetch'

const GlobalContext = React.createContext()

const operator = process.env.REACT_APP_OPERATOR

// locales is set as an env var
// e.g. REACT_APP_LANGUAGES=nb,en
const locales = process.env.REACT_APP_LANGUAGES.split(',')

const checkCredentials = (fetch, credentials) => {
  const opts = options({
    method: 'POST',
    body: {
      username: credentials.username,
      password: credentials.password,
      operator_id: operator,
    },
  })

  return fetch(routes.auth, opts).then((res) => result(res))
}

const resetPassword = (fetch, username, url, locale) => {
  const opts = options({
    method: 'POST',
    body: {
      operator_id: operator,
      username: username,
      url: url,
      locale: locale,
    },
  })

  return fetch(routes.resetPassword, opts).then((res) => result(res))
}

const updatePassword = (fetch, token, password) => {
  const opts = options({
    method: 'POST',
    body: {
      operator_id: operator,
      token: token,
      password: password,
    },
  })

  return fetch(routes.updatePassword, opts).then((res) => result(res))
}

const Translation = ({ k, t, vars }) => {
  // We want \n to actually be line breaks
  const style = {
    whiteSpace: 'pre-line',
  }

  return <span style={style}>{t(k, vars)}</span>
}

Translation.propTypes = {
  k: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  t: PropTypes.func.isRequired,
  vars: PropTypes.object.isRequired,
}

Translation.defaultProps = {
  vars: {},
}

const withGlobal = (fetch) => {
  const GlobalStateComponent = (props) => {
    // Set token from URL fragment (#access_token=…) or, transitionally, the
    // search params (?access_token=…). The fragment form is preferred because
    // fragments are never sent to the server, so the token never appears in
    // any ingress / load-balancer / CDN access log.
    //
    // The query-param read remains for backwards-compat with signup-ui-no
    // success links emitted before the fragment swap rolled out. Remove the
    // searchParams half once that build is no longer in the wild.
    const url = new URL(window.location.href)
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
    const accessToken =
      hashParams.get('access_token') || url.searchParams.get('access_token')

    if (accessToken) {
      localStorage.setItem('token', accessToken)
      // remove the token from the url to prevent sharing
      hashParams.delete('access_token')
      url.searchParams.delete('access_token')
      const remaining = hashParams.toString()
      url.hash = remaining ? `#${remaining}` : ''
      window.history.replaceState(null, '', url.toString())
    }

    const { t } = useTranslation()
    const [token, setToken] = useState(localStorage.getItem('token'))
    const [theme, setTheme] = useState(process.env.REACT_APP_THEME)

    let l = localStorage.getItem('locale')
    if (locales.indexOf(l) === -1) {
      l = locales[0]
      localStorage.setItem('locale', l)
    }
    const [locale, setLocale] = useState(l)

    // jwt-redesign rung 4: customer tokens now expire in 15 min. Keep an active
    // session alive by sliding it: every ~12 min, exchange the still-valid token
    // for a fresh one via /auth/refresh. Reads the token from localStorage at
    // tick time (the source of truth) to avoid a stale closure; re-running on
    // [token] resets the timer after each refresh. Any failure (expired /
    // invalid / network) drops the session so the user re-logs in.
    useEffect(() => {
      if (!token) return undefined

      const REFRESH_INTERVAL_MS = 12 * 60 * 1000 // must stay < the 15-min token TTL
      const id = setInterval(() => {
        const current = localStorage.getItem('token')
        if (!current) return

        fetch(routes.refresh, options({ method: 'POST', token: 'Bearer ' + current }))
          .then(result)
          .then((res) => {
            if (res && res.token) {
              localStorage.setItem('token', res.token)
              setToken(res.token)
            }
          })
          .catch(() => {
            localStorage.removeItem('token')
            setToken(null)
          })
      }, REFRESH_INTERVAL_MS)

      return () => clearInterval(id)
    }, [token])

    const trans = (k, vars = {}) => <Translation k={k} t={t} vars={vars} />

    let env = 'off'
    if (/me.*/.test(window.location.origin)) {
      env = 'production'
    } else if (/.*staging.*/.test(window.location.origin)) {
      env = 'staging'
    } else if (/.*localhost.*/.test(window.location.origin)) {
      env = 'test'
    }

    const ctx = {
      t,
      locale,
      env,
      token,
      theme,
      setTheme,
      locales,
      checkCredentials: (credentials) => {
        return checkCredentials(fetch, credentials).then((res) => {
          localStorage.setItem('token', res.token)
          setToken(localStorage.getItem('token'))
        })
      },
      clearToken: () => {
        localStorage.removeItem('token')
        setToken(localStorage.getItem('token'))
      },
      setLocale: (locale) => {
        localStorage.setItem('locale', locale)
        setLocale(locale)
      },
      resetPassword: (username, url) =>
        resetPassword(fetch, username, url, locale),
      updatePassword: (token, password) =>
        updatePassword(fetch, token, password),
      T: ({ t, vars }) => trans(t, vars),
    }

    return (
      <GlobalContext.Provider value={ctx}>
        <Layout theme={theme} t={t} />
        {props.children}
      </GlobalContext.Provider>
    )
  }

  GlobalStateComponent.propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
    ]).isRequired,
  }

  return GlobalStateComponent
}

export default withGlobal(fetch)

export { GlobalContext }
